/**
 * AccordionGroup - Reusable accordion component with consistent styling
 *
 * Features:
 * - Collapsible header with icon, title, count badge
 * - Smooth expand/collapse animation
 * - Customizable colors and icons
 * - Optional summary content in header
 *
 * Usage:
 * <AccordionGroup
 *   id="maintenance"
 *   title="Maintenance"
 *   count={5}
 *   icon={WrenchIcon}
 *   color="amber"
 *   isExpanded={isExpanded('maintenance')}
 *   onToggle={() => toggleGroup('maintenance')}
 * >
 *   {children}
 * </AccordionGroup>
 */
import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Color schemes for accordion headers
const COLOR_SCHEMES = {
  amber: {
    bg: 'bg-amber-50 hover:bg-amber-100',
    border: 'border-amber-200',
    text: 'text-amber-900',
    icon: 'text-amber-600',
    badge: 'bg-amber-200 text-amber-800'
  },
  blue: {
    bg: 'bg-blue-50 hover:bg-blue-100',
    border: 'border-blue-200',
    text: 'text-blue-900',
    icon: 'text-blue-600',
    badge: 'bg-blue-200 text-blue-800'
  },
  emerald: {
    bg: 'bg-emerald-50 hover:bg-emerald-100',
    border: 'border-emerald-200',
    text: 'text-emerald-900',
    icon: 'text-emerald-600',
    badge: 'bg-emerald-200 text-emerald-800'
  },
  red: {
    bg: 'bg-red-50 hover:bg-red-100',
    border: 'border-red-200',
    text: 'text-red-900',
    icon: 'text-red-600',
    badge: 'bg-red-200 text-red-800'
  },
  purple: {
    bg: 'bg-purple-50 hover:bg-purple-100',
    border: 'border-purple-200',
    text: 'text-purple-900',
    icon: 'text-purple-600',
    badge: 'bg-purple-200 text-purple-800'
  },
  orange: {
    bg: 'bg-orange-50 hover:bg-orange-100',
    border: 'border-orange-200',
    text: 'text-orange-900',
    icon: 'text-orange-600',
    badge: 'bg-orange-200 text-orange-800'
  },
  slate: {
    bg: 'bg-slate-50 hover:bg-slate-100',
    border: 'border-slate-200',
    text: 'text-slate-900',
    icon: 'text-slate-600',
    badge: 'bg-slate-200 text-slate-800'
  },
  gray: {
    bg: 'bg-gray-50 hover:bg-gray-100',
    border: 'border-gray-200',
    text: 'text-gray-900',
    icon: 'text-gray-600',
    badge: 'bg-gray-200 text-gray-800'
  }
};

export function AccordionGroup({
  id,
  title,
  count,
  icon: Icon,
  color = 'gray',
  isExpanded = false,
  onToggle,
  children,
  summary,
  headerRight,
  className = '',
  contentClassName = '',
  emptyMessage
}) {
  const { t } = useTranslation();
  const scheme = COLOR_SCHEMES[color] || COLOR_SCHEMES.gray;

  return (
    <div className={`border rounded-lg overflow-hidden ${scheme.border} ${className}`}>
      {/* Accordion Header */}
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between p-3 ${scheme.bg} transition-colors duration-150`}
      >
        <div className="flex items-center gap-3">
          {/* Expand/Collapse Chevron */}
          {isExpanded ? (
            <ChevronDown className={`w-5 h-5 ${scheme.icon}`} />
          ) : (
            <ChevronRight className={`w-5 h-5 ${scheme.icon}`} />
          )}

          {/* Icon */}
          {Icon && <Icon className={`w-5 h-5 ${scheme.icon}`} />}

          {/* Title */}
          <span className={`font-medium ${scheme.text}`}>{title}</span>

          {/* Count Badge */}
          {typeof count === 'number' && (
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${scheme.badge}`}>
              {count}
            </span>
          )}

          {/* Optional Summary */}
          {summary && (
            <span className="text-sm text-gray-500 ml-2">{summary}</span>
          )}
        </div>

        {/* Optional Right Content (e.g., totals, actions) */}
        {headerRight && (
          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            {headerRight}
          </div>
        )}
      </button>

      {/* Accordion Content */}
      <div
        className={`transition-all duration-200 ease-in-out ${
          isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className={`p-3 bg-white ${contentClassName}`}>
          {count === 0 ? (
            <p className="text-sm text-gray-500 italic text-center py-4">{emptyMessage ?? t('common.states.noItem')}</p>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * AccordionContainer - Wrapper for multiple AccordionGroups with consistent spacing
 */
export function AccordionContainer({ children, className = '' }) {
  return (
    <div className={`space-y-3 ${className}`}>
      {children}
    </div>
  );
}

/**
 * NestedAccordion - Smaller accordion for nested content within an AccordionGroup
 */
export function NestedAccordion({
  title,
  count,
  icon: Icon,
  isExpanded = false,
  onToggle,
  children,
  className = ''
}) {
  return (
    <div className={`border border-gray-200 rounded-md overflow-hidden ${className}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 transition-colors duration-150"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
          {Icon && <Icon className="w-4 h-4 text-gray-500" />}
          <span className="text-sm font-medium text-gray-700">{title}</span>
          {typeof count === 'number' && (
            <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-700 rounded">
              {count}
            </span>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="p-2 bg-white border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
}

export default AccordionGroup;
